/**
 * Lookahead policy for the mosaic tile layer — the "prefetch ring".
 *
 * Leaflet's `GridLayer._update` asks for exactly the tiles inside
 * `_getTiledPixelBounds(center)`. `keepBuffer` only decides which tiles that
 * have *already been created* survive pruning; it never creates one. Measured
 * 2026-10-21 on the real page: `keepBuffer: 3` moved the pan gap from 13/44
 * frames to 10/44, because the ring was never fetched in the first place.
 *
 * So the lever is the layer's own idea of the viewport: widen its pixel bounds
 * and Leaflet fetches the ring, and keeps it, because a tile inside those
 * bounds is `current`.
 *
 * The width is measured in whole tiles, not viewport fractions. A fraction
 * under-delivers exactly where it matters: at the native zoom a phone viewport
 * is under one tile wide, so half a viewport of padding rounds to the same tile
 * range Leaflet already fetches (measured 2026-11-02: zero extra requests).
 * Whole tiles are also what a pan consumes, so "two tiles of lookahead" means
 * the same thing on every screen.
 */

/** Tiles of lookahead per side. A phone at the browsing zoom shows about one
 *  tile across, so two tiles of ring covers a half-screen flick in full at that
 *  zoom and a whole tile past the edge at native. Measured 2026-11-02 at 51 CSS
 *  px per cell: the share of a half-screen pan already decoded went 0.53 → 1.00
 *  right and 0.72 → 1.00 down. */
export const PREFETCH_TILES = 2

/** Ceiling on the *extra* requests a ring may add. At 4K, two tiles per side
 *  is hundreds of tiles, and at coarse zooms each one is a Vix atlas join. The
 *  ring narrows rather than spend that. */
export const PREFETCH_MAX_EXTRA = 64

/**
 * The ring to hand the layer, in whole tiles per side (0 for the tight bounds
 * Leaflet ships with). `cols`/`rows` are the viewport measured in tiles.
 *
 * Starts from `tiles` and steps down one at a time, keeping the widest that
 * fits `maxExtra`. A viewport so large that even one tile per side is over the
 * cap gets 0, i.e. the old behaviour: it already spends a lot per pan by
 * virtue of its size.
 */
export function ringFor({cols = 0, rows = 0, tiles = PREFETCH_TILES, maxExtra = PREFETCH_MAX_EXTRA} = {}) {
  if (!(cols >= 1e-6) || !(rows >= 1e-6)) return 0

  const c = Math.max(1, Math.ceil(cols))
  const r = Math.max(1, Math.ceil(rows))

  for (let pad = Math.max(1, Math.floor(tiles)); pad >= 1; pad--) {
    if (ringTileEstimate({cols: c, rows: r, pad}).extra <= maxExtra) return pad
  }
  return 0
}

/** Tile count for a `cols`×`rows` viewport widened by `pad` tiles per side. */
export function ringTileEstimate({cols, rows, pad = PREFETCH_TILES}) {
  const c = Math.max(1, Math.ceil(cols))
  const r = Math.max(1, Math.ceil(rows))
  const visible = c * r
  const total = (c + 2 * pad) * (r + 2 * pad)
  return {visible, total, extra: total - visible}
}

/**
 * Whether to look ahead at all. Explicit "reduce data" wins, and a 2G-class
 * link keeps the old behaviour: a looked-ahead tile the visitor never pans to
 * is pure waste on a metered connection.
 */
export function prefetchAllowed({saveData = false, effectiveType = null} = {}) {
  if (saveData) return false
  return !(effectiveType === "slow-2g" || effectiveType === "2g")
}

export default {
  PREFETCH_MAX_EXTRA,
  PREFETCH_TILES,
  prefetchAllowed,
  ringFor,
  ringTileEstimate,
}
